import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Workflow, ArrowUp, ArrowDown, GraduationCap, ListChecks, ClipboardList } from "lucide-react";
import ProgramPagesEditor from "@/pages/admin/ProgramPagesEditor";
import TaskTemplates from "@/pages/admin/TaskTemplates";

const TOGGLES = [
  ["required", "Required"],
  ["candidate_visible", "Candidate Visible"],
  ["document_required", "Doc Required"],
  ["manual_verification", "Manual Verify"],
  ["active", "Active"],
];

const TABS = [
  { key: "templates", label: "Milestone Templates", Icon: ListChecks },
  { key: "tasks", label: "Task Templates", Icon: ClipboardList },
  { key: "pages", label: "Information Pages", Icon: GraduationCap },
];

export default function Programs() {
  const { isAdmin, isGroupAdmin, isSuper } = useAuth();
  const canEditPages = isAdmin || isGroupAdmin || isSuper;
  const [tab, setTab] = useState("templates");

  return (
    <div>
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Workflow className="w-5 h-5 text-[#0D9488]" /> Candidate Programs</h1>
        <p className="text-sm text-slate-500 mt-0.5">Configure milestone checklist templates and the candidate-facing program information pages.</p>
      </div>
      <div className="flex gap-1 border-b border-slate-200 mt-4">
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`programs-tab-${key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? "border-[#006090] text-[#006090]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>
      {tab === "templates" ? <TemplatesTab /> : tab === "tasks" ? <TaskTemplates /> : <ProgramPagesEditor canEdit={canEditPages} />}
    </div>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [versionId, setVersionId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/programs/templates").then((r) => {
      setTemplates(r.data.items || []);
      const first = (r.data.items || []).flatMap((t) => t.versions || [])[0];
      if (first) setVersionId(first.id);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const load = useCallback(() => {
    if (!versionId) return;
    api.get(`/programs/versions/${versionId}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [versionId]);
  useEffect(() => { load(); }, [load]);

  const patchReq = async (rid, body) => {
    try { await api.patch(`/programs/requirements/${rid}`, body); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const patchPhase = async (pid, body) => {
    try { await api.patch(`/programs/phases/${pid}`, body); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const versionOptions = templates.flatMap((t) => (t.versions || []).map((v) => ({ id: v.id, label: `${t.name} — v${v.version}${v.is_default ? " (default)" : ""}` })));
  const reqsByPhase = (pid) => (data?.requirements || []).filter((r) => r.phase_id === pid).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const swap = async (list, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    await Promise.all([
      api.patch(`/programs/requirements/${list[i].id}`, { display_order: list[j].display_order }),
      api.patch(`/programs/requirements/${list[j].id}`, { display_order: list[i].display_order }),
    ]).catch((e) => toast.error(apiError(e)));
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <p className="text-sm text-slate-500">Edit phases, requirements, order, visibility and Key-Contact links. Existing enrollments keep their version.</p>
        <Select value={versionId} onValueChange={setVersionId}>
          <SelectTrigger className="w-72" data-testid="programs-version-select"><SelectValue placeholder="Select template version" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">{versionOptions.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!data ? <p className="text-sm text-slate-400 mt-8">Loading…</p> : (
        <div className="mt-6 space-y-5">
          {(data.phases || []).map((ph) => {
            const reqs = reqsByPhase(ph.id);
            return (
              <div key={ph.id} className="rounded-xl border border-slate-200 bg-white" data-testid={`prog-phase-${ph.id}`}>
                <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl">
                  <Input defaultValue={ph.name} onBlur={(e) => e.target.value !== ph.name && patchPhase(ph.id, { name: e.target.value })}
                    className="max-w-xs font-semibold" data-testid={`prog-phase-name-${ph.id}`} />
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={ph.active !== false} onCheckedChange={(v) => patchPhase(ph.id, { active: v })} data-testid={`prog-phase-active-${ph.id}`} /> Active</label>
                </div>
                <div className="px-4 py-3 border-b border-slate-100">
                  <label className="text-xs font-medium text-slate-500">Candidate-facing stage description (shown in Candidate Portal → My Journey)</label>
                  <Textarea defaultValue={ph.candidate_description || ""} rows={2} placeholder="e.g. During this stage you'll complete the licensing, business preparation and staffing steps needed to move forward."
                    onBlur={(e) => e.target.value !== (ph.candidate_description || "") && patchPhase(ph.id, { candidate_description: e.target.value })}
                    className="mt-1 text-sm" data-testid={`prog-phase-desc-${ph.id}`} />
                </div>
                <div className="divide-y divide-slate-100">
                  {reqs.map((r, i) => (
                    <div key={r.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`prog-req-${r.id}`}>
                      <div className="flex flex-col">
                        <button disabled={i <= 0} onClick={() => swap(reqs, i, -1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button disabled={i >= reqs.length - 1} onClick={() => swap(reqs, i, 1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                      </div>
                      <Input defaultValue={r.title} onBlur={(e) => e.target.value !== r.title && patchReq(r.id, { title: e.target.value })} className="flex-1 min-w-[200px] text-sm" data-testid={`prog-req-title-${r.id}`} />
                      {TOGGLES.map(([k, lbl]) => (
                        <label key={k} className="flex items-center gap-1.5 text-[11px] text-slate-600"><Switch checked={!!r[k]} onCheckedChange={(v) => patchReq(r.id, { [k]: v })} data-testid={`prog-req-${k}-${r.id}`} /> {lbl}</label>
                      ))}
                      <Input defaultValue={r.key_contact_topic || ""} placeholder="Key Contact topic" onBlur={(e) => (e.target.value || null) !== (r.key_contact_topic || null) && patchReq(r.id, { key_contact_topic: e.target.value || null })} className="w-48 text-xs" data-testid={`prog-req-kc-${r.id}`} />
                      {r.applicable_program_subtype && <span className="text-[10px] text-[#0D9488] border border-[#0D9488]/30 rounded px-1">{r.applicable_program_subtype}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
